import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  Box,
  MenuItem,
  Divider,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  Chip,
} from "@mui/material";
import { Close, DirectionsCar, ArrowForward } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { selectStatusOptions } from "../../redux/slices/statusDefinitionsSlice";

const VehicleStatusDialog = ({
  open,
  onClose,
  onSubmit,
  vehicle,
  loading = false,
}) => {
  const statusOptions = useSelector(selectStatusOptions("vehicle"));
  const [isCustom, setIsCustom] = useState(false);
  const [status, setStatus] = useState("");
  const [customLabel, setCustomLabel] = useState("");
  const [note, setNote] = useState("");

  // Reset fields every time the dialog opens fresh, not just on close —
  // otherwise a leftover selection from a previous open could linger.
  useEffect(() => {
    if (open) {
      setIsCustom(false);
      setStatus("");
      setCustomLabel("");
      setNote("");
    }
  }, [open]);

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = () => {
    if (isCustom) {
      onSubmit({
        status: `custom_${Date.now()}`,
        label: customLabel.trim(),
        note: note.trim(),
        isCustom: true,
      });
    } else {
      const found = statusOptions.find((s) => s.value === status);
      onSubmit({
        status,
        label: found?.label || status,
        note: note.trim(),
        isCustom: false,
      });
    }
  };

  const canSubmit = isCustom
    ? customLabel.trim().length > 0
    : status !== "" && status !== vehicle?.status;

  if (!vehicle) return null;

  // Exclude the current status from the pickable list entirely — showing
  // it disabled inline was confusing. It's already displayed prominently
  // above the dropdown instead.
  const selectableOptions = statusOptions.filter(
    (s) => s.value !== vehicle.status,
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Typography fontWeight={700}>Update Vehicle Status</Typography>
        <IconButton size="small" onClick={handleClose}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* Vehicle identity */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
          }}
        >
          <DirectionsCar sx={{ color: "text.secondary", fontSize: 20 }} />
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {vehicle.vehicle?.year} {vehicle.vehicle?.make}{" "}
              {vehicle.vehicle?.model}
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontFamily: "monospace", color: "primary.main" }}
            >
              {vehicle.trackingId}
            </Typography>
          </Box>
        </Box>

        {/* Current -> New status flow */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            bgcolor: "action.hover",
            borderRadius: 2,
            p: 1.75,
            mb: 2.5,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              Current status
            </Typography>
            <Chip
              label={vehicle.statusLabel || vehicle.status}
              size="small"
              sx={{ fontWeight: 700, bgcolor: "background.paper", border: "1px solid", borderColor: "divider" }}
            />
          </Box>
          <ArrowForward sx={{ color: "text.disabled", fontSize: 18, flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              New status
            </Typography>
            {isCustom ? (
              <Chip
                label={customLabel.trim() || "Not set"}
                size="small"
                color={customLabel.trim() ? "primary" : "default"}
                sx={{ fontWeight: 700 }}
              />
            ) : status ? (
              <Chip
                label={statusOptions.find((s) => s.value === status)?.label}
                size="small"
                color="primary"
                sx={{ fontWeight: 700 }}
              />
            ) : (
              <Typography variant="caption" color="text.disabled" sx={{ fontStyle: "italic" }}>
                Not selected yet
              </Typography>
            )}
          </Box>
        </Box>

        {/* Custom toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={isCustom}
              onChange={(e) => {
                setIsCustom(e.target.checked);
                setStatus("");
                setCustomLabel("");
              }}
              size="small"
            />
          }
          label={
            <Typography variant="body2" fontWeight={600}>
              Use custom status
            </Typography>
          }
          sx={{ mb: 2 }}
        />

        {/* Predefined */}
        {!isCustom && (
          <TextField
            select
            fullWidth
            label="Select new status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            size="small"
            sx={{ mb: 2 }}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="" disabled>
              <Typography color="text.disabled">Choose a status…</Typography>
            </MenuItem>
            {selectableOptions.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>
        )}

        {/* Custom */}
        {isCustom && (
          <>
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2, py: 0.75 }}>
              <Typography variant="caption">
                Custom statuses let you set any message — e.g. "Waiting for
                title transfer" or "Damage inspection in progress"
              </Typography>
            </Alert>
            <TextField
              fullWidth
              label="Custom Status Label *"
              value={customLabel}
              onChange={(e) => setCustomLabel(e.target.value)}
              size="small"
              placeholder="e.g. Waiting for title transfer"
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 80 }}
            />
          </>
        )}

        <Divider sx={{ mb: 2 }} />

        <TextField
          fullWidth
          multiline
          rows={2}
          label="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          size="small"
          placeholder="Add context for this status update — visible on the tracking page"
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit || loading}
        >
          {loading ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            "Update Status"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VehicleStatusDialog;